import databaseService from './database.service';

/**
 * Client Service for ZyloBill Web App
 * Adapted from desktop app to work with browser PouchDB
 */

export const clientService = {
  /**
   * List all clients with invoice statistics
   */
  async list() {
    const clients = await databaseService.getClients();
    
    // Get all invoices to calculate statistics
    const invoices = await databaseService.getInvoices();
    
    // Calculate statistics for each client
    const clientsWithStats = clients.map(client => {
      const clientInvoices = invoices.filter(inv => 
        inv.client_id === client._id && !inv.deleted && !inv.archived_at
      );
      
      const invoiceCount = clientInvoices.length;
      const totalBilled = clientInvoices.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
      
      return {
        ...client,
        id: client._id,
        invoiceCount,
        totalBilled: Math.round(totalBilled),
      };
    });
    
    // Sort by name
    clientsWithStats.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    return clientsWithStats;
  },

  /**
   * Get client picker list (for dropdowns)
   */
  async picker(params = {}) {
    const clients = await this.list();
    
    // Filter by search term if provided
    if (params.search) {
      const search = params.search.toLowerCase();
      return clients.filter(client => 
        (client.name || '').toLowerCase().includes(search) ||
        (client.email || '').toLowerCase().includes(search) ||
        (client.phone || '').toLowerCase().includes(search)
      );
    }

    return clients;
  },

  /**
   * Add new client
   */
  async add(data) {
    const clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const client = {
      _id: clientId,
      type: 'client',
      name: data.name || '',
      email: data.email || '',
      phone: data.phone || '',
      gstin: data.gstin || '',
      billing_address: data.billing_address || '',
      city: data.city || '',
      state: data.state || '',
      pincode: data.pincode || '',
      country: data.country || 'India',
      deleted: false,
    };

    const result = await databaseService.createDocument(client);
    
    return {
      ...client,
      id: result.id,
    };
  },

  /**
   * Update client
   */
  async update(id, data) {
    const currentClient = await databaseService.readDocument(id);
    
    if (!currentClient) {
      throw new Error('Client not found');
    }

    const updatedClient = {
      ...currentClient,
      name: data.name !== undefined ? data.name : currentClient.name,
      email: data.email !== undefined ? data.email : currentClient.email,
      phone: data.phone !== undefined ? data.phone : currentClient.phone,
      gstin: data.gstin !== undefined ? data.gstin : currentClient.gstin,
      billing_address: data.billing_address !== undefined ? data.billing_address : currentClient.billing_address,
      city: data.city !== undefined ? data.city : currentClient.city,
      state: data.state !== undefined ? data.state : currentClient.state,
      pincode: data.pincode !== undefined ? data.pincode : currentClient.pincode,
      country: data.country !== undefined ? data.country : currentClient.country,
    };

    await databaseService.updateDocument(updatedClient);
    
    return {
      ...updatedClient,
      id: updatedClient._id,
    };
  },

  /**
   * Delete client (soft delete)
   */
  async delete(id) {
    await databaseService.deleteDocument(id);
    return { success: true };
  },

  /**
   * Archive client
   */
  async archive(id) {
    const currentClient = await databaseService.readDocument(id);
    
    if (!currentClient) {
      throw new Error('Client not found');
    }

    const archivedClient = {
      ...currentClient,
      archived_at: new Date().toISOString(),
    };

    await databaseService.updateDocument(archivedClient);
    
    return {
      ...archivedClient,
      id: archivedClient._id,
    };
  },

  /**
   * Restore archived client
   */
  async restore(id) {
    const currentClient = await databaseService.readDocument(id);
    
    if (!currentClient) {
      throw new Error('Client not found');
    }

    const restoredClient = {
      ...currentClient,
      archived_at: null,
    };

    await databaseService.updateDocument(restoredClient);
    
    return {
      ...restoredClient,
      id: restoredClient._id,
    };
  },

  /**
   * Get client by ID
   */
  async get(id) {
    const client = await databaseService.readDocument(id);
    
    if (!client) {
      return null;
    }

    return {
      ...client,
      id: client._id,
    };
  },
};
